import { useState } from 'react';
import { Mail, Check, Share2 } from 'lucide-react';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { apiService } from '@/lib/api';
import { nativeShare } from '@/lib/capacitor';
import { tapToPayName } from '@/lib/tapToPay';

/**
 * Digital receipt for a Tap to Pay transaction.
 *
 * Apple's Tap to Pay on iPhone App Review checklist (§5.9): "Regardless of whether
 * a transaction is approved or declined, it must be possible to send a confidential
 * digital receipt to the customer." Hence `outcome` — this renders on the decline
 * screen too, where the app's existing invoice-sending has nothing to send.
 *
 * Two delivery routes, because email alone can leave a merchant with none. Sending
 * goes through the company's configured Gmail/SES sender, and `POST /email/send`
 * 400s outright when a company has configured neither — so the share sheet (Apple
 * accepts "Activity views") is always offered as a fallback.
 */
export function TapToPayReceipt({
    amount,
    outcome,
    invoiceNumber,
    defaultEmail,
}: {
    amount: number;
    outcome: 'approved' | 'declined';
    invoiceNumber?: string;
    defaultEmail?: string;
}) {
    const [email, setEmail] = useState(defaultEmail ?? '');
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
    const [error, setError] = useState<string | null>(null);

    const when = new Date().toLocaleString();
    const jobRef = invoiceNumber ? ` for invoice ${invoiceNumber}` : '';

    const subject = outcome === 'approved'
        ? `Payment receipt — $${amount.toFixed(2)}`
        : `Payment declined — $${amount.toFixed(2)}`;

    const body = outcome === 'approved'
        ? `Thank you. Your payment of $${amount.toFixed(2)}${jobRef} was approved on ${when}.\n\nPaid by contactless card using ${tapToPayName()}.`
        : `Your card was declined for $${amount.toFixed(2)}${jobRef} on ${when}. No money was taken.\n\nPlease try another payment method.`;

    const share = async () => {
        setError(null);
        const result = await nativeShare({ title: subject, text: `${subject}\n\n${body}` });
        if (result === 'shared') {
            setStatus('sent');
        } else if (result === 'unavailable') {
            setError('Sharing is not available on this device.');
        }
        // 'cancelled' is the user backing out — not an error worth reporting.
    };

    const send = async () => {
        // Trust boundary: this address leaves the device, so validate before sending
        // a transaction record to it.
        const trimmed = email.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            setStatus('error');
            setError('Enter a valid email address.');
            return;
        }

        setStatus('sending');
        setError(null);
        try {
            await apiService.sendEmail({ to: trimmed, subject, body, email_context: 'invoice' });
            setStatus('sent');
        } catch {
            setStatus('error');
            // Most likely this company has configured no Gmail/SES sender at all, in
            // which case retrying the address will never work — point at the sheet.
            setError('Could not email the receipt. Use Share instead, or check the address.');
        }
    };

    if (status === 'sent') {
        return (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-green-700">
                <Check className="h-4 w-4 flex-shrink-0" />
                Receipt sent
            </div>
        );
    }

    return (
        <div className="mt-4 border-t border-gray-100 pt-4">
            <label className="flex items-center gap-2 text-xs font-medium text-gray-600 mb-2">
                <Mail className="h-3.5 w-3.5" />
                Email a receipt to the customer
            </label>
            <div className="flex gap-2">
                <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="customer@example.com"
                    className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500"
                />
                <button
                    type="button"
                    onClick={send}
                    disabled={status === 'sending'}
                    className="flex items-center justify-center gap-1.5 px-4 py-2 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {status === 'sending' && <LoadingSpinner />}
                    Send
                </button>
            </div>
            {error && <p className="mt-2 text-xs text-red-500">{error}</p>}

            {/* Always available, so a receipt is possible even for merchants whose
                company has no email sender configured (Apple §5.9 accepts this). */}
            <button
                type="button"
                onClick={share}
                className="mt-2 flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
                <Share2 className="h-3.5 w-3.5" />
                Share receipt another way
            </button>
        </div>
    );
}
